import React from "react";

import * as RadixTooltip from "@radix-ui/react-tooltip";
import clsx from "clsx";

export type TooltipProps = {
  children: React.ReactNode;
  content: React.ReactNode;
  position?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right"
    | "left"
    | "right";
  disable?: boolean;
  className?: string;
  arrowClassName?: string;
} & Omit<RadixTooltip.TooltipContentProps, "content">;

const positionMap: Record<
  NonNullable<TooltipProps["position"]>,
  {
    side: RadixTooltip.TooltipContentProps["side"];
    align: RadixTooltip.TooltipContentProps["align"];
    alignOffset?: number | undefined;
  }
> = {
  "top-left": { side: "top", align: "start", alignOffset: -5 },
  "top-center": { side: "top", align: "center" },
  "top-right": { side: "top", align: "end" },
  "bottom-left": { side: "bottom", align: "start" },
  "bottom-center": { side: "bottom", align: "center" },
  "bottom-right": { side: "bottom", align: "end" },
  left: { side: "left", align: "center" },
  right: { side: "right", align: "center" }
};

function Tooltip(props: TooltipProps) {
  const {
    children,
    content,
    position = "top-center",
    disable = false,
    className,
    arrowClassName,
    sideOffset = 8,
    ...optionalProps
  } = props;

  if (disable) return <>{children}</>;

  const { side, align, alignOffset } = positionMap[position];

  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          className={clsx(
            "z-[1000] px-3 py-2 text-[10px] text-white bg-black rounded-xl shadow-lg",
            "animate-fade-in data-[state=delayed-open]:fade-in data-[state=closed]:fade-out",
            "max-w-xs",
            className
          )}
          {...optionalProps}
        >
          {content}
          <RadixTooltip.Arrow className={clsx("fill-black", arrowClassName)} />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}

export default Tooltip;
